import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useTransition,
  useCallback,
  useRef,
  memo,
  useMemo
} from 'react';
import CalendarPanel from './CalendarPanel';
import HomeMobileList from './HomeMobileList';
import SpaceSwitcher, { getSpaceOptionId } from './SpaceSwitcher';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import {
  filterBookingsBySpace,
  filterBusyTimesBySpace,
  getBookingSummaryCounts,
  getBusyTimesOverlappingDate
} from '../utils/bookingUtils';

const ManageBookingsView = lazy(() => import('./ManageBookingsView'));
const BookingsTimeline = lazy(() => import('./BookingsTimeline'));
const HydratedManageBookingDrawer = lazy(() => import('./HydratedManageBookingDrawer'));
const ManageBookingMobile = lazy(() => import('./ManageBookingMobile'));

const TimelineFallback = () => (
  <div className="bookings-timeline-loading" aria-busy="true">
    <div className="loading-spinner" />
    <p>Loading calendar…</p>
  </div>
);

const ManageBookingsFallback = () => (
  <div className="manage-bookings-loading-shell" aria-busy="true">
    <div className="loading-spinner" />
    <p>Opening manage bookings…</p>
  </div>
);

const MANAGE_BOOKINGS_OPEN_KEY_PREFIX = 'venue-dashboard-manage-bookings-open';
const MANAGE_BOOKINGS_INITIAL_ID_KEY_PREFIX = 'venue-dashboard-manage-bookings-initial-id';

const getScopedSessionKey = (prefix, venueId) => `${prefix}:${String(venueId || 'default')}`;

const SELECTED_SPACE_KEY_PREFIX = 'venue-dashboard-selected-space';

const pickInitialSpaceId = (venueId, venueSpaces, preferredSpaceId) => {
  if (!venueSpaces?.length) return preferredSpaceId || null;
  const ids = venueSpaces.map(getSpaceOptionId).filter(Boolean);
  if (!ids.length) return null;
  if (preferredSpaceId && ids.includes(String(preferredSpaceId))) {
    return String(preferredSpaceId);
  }
  try {
    const stored = sessionStorage.getItem(getScopedSessionKey(SELECTED_SPACE_KEY_PREFIX, venueId));
    if (stored && ids.includes(stored)) return stored;
  } catch {
    // ignore
  }
  return ids[0];
};

export const clearManageBookingsSession = (venueId) => {
  try {
    sessionStorage.removeItem(getScopedSessionKey(MANAGE_BOOKINGS_OPEN_KEY_PREFIX, venueId));
    sessionStorage.removeItem(getScopedSessionKey(MANAGE_BOOKINGS_INITIAL_ID_KEY_PREFIX, venueId));
  } catch {
    // no-op if sessionStorage unavailable
  }
};

const useIsPhone = () => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1024px)');
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return matches;
};

const HomeSectionActive = memo(function HomeSectionActive({
  isVisible = true,
  venueId,
  spaceId,
  venueSpaces,
  defaultPrice,
  selectedDate,
  onDateSelect,
  bookings,
  bookingsByDateIndex = null,
  bookingSummaryCounts = null,
  selectedDateBusyTimes,
  busyTimes,
  bookingsLoading,
  bookingsError,
  onRetryBookings,
  onEnsurePastWindow,
  onLoadPastBookings,
  loadingPastBookings = false,
  hasMorePastBookings = false,
  onBookingHydrated,
  onConfirmBooking,
  onPatchBookingLocal,
  onAddBookingLocal,
  venueDetails = null,
  syncCalendar,
  syncLoading,
  pushCalendarToGoogle,
  pushLoading = false,
  lastSyncTime,
  availabilityRefreshTick,
  availabilityRefreshing,
  onRefreshAvailability,
  operatingHours = {},
  operatingHoursPerPerson = {},
  holidays = [],
  dynamicPricing = [],
  defaultPerPersonPrice = null,
  dynamicPricingPerPerson = [],
  bookingRules = null,
  homeViewKey = 0,
  onOpenMessages,
  onSectionChange
}) {
  const [isManageBookingsOpen, setIsManageBookingsOpen] = useState(false);
  const [manageViewMounted, setManageViewMounted] = useState(false);
  const [initialBookingId, setInitialBookingId] = useState(null);
  const [drawerBooking, setDrawerBooking] = useState(null);
  const [mobileManage, setMobileManage] = useState(null);
  const isPhone = useIsPhone();
  const [addBookingSignal, setAddBookingSignal] = useState(0);
  const [bookingsRefreshing, setBookingsRefreshing] = useState(false);
  const [, startUiTransition] = useTransition();
  const [selectedSpaceId, setSelectedSpaceId] = useState(() =>
    pickInitialSpaceId(venueId, venueSpaces, spaceId)
  );

  useEffect(() => {
    setSelectedSpaceId(pickInitialSpaceId(venueId, venueSpaces, spaceId));
  }, [venueId, venueSpaces, spaceId]);

  const handleSpaceChange = useCallback(
    (nextSpaceId) => {
      setSelectedSpaceId(nextSpaceId);
      try {
        sessionStorage.setItem(
          getScopedSessionKey(SELECTED_SPACE_KEY_PREFIX, venueId),
          String(nextSpaceId)
        );
      } catch {
        // ignore
      }
    },
    [venueId]
  );

  const multiSpace = (venueSpaces?.length || 0) > 1;
  const multiSpaceCalendarsEnabled = Boolean(
    venueDetails?.multi_space_calendars_enabled ??
      venueDetails?.multiSpaceCalendarsEnabled ??
      multiSpace
  );
  const venueTimezone =
    venueDetails?.timezone || venueDetails?.time_zone || 'Europe/London';
  /** Per-space busy rows from GET …/busy-times?space_id= (avoids flash on space switch). */
  const [spaceBusyById, setSpaceBusyById] = useState({});

  useEffect(() => {
    if (!multiSpace || !venueId || !selectedSpaceId) {
      return undefined;
    }
    let cancelled = false;
    const spaceAtRequest = String(selectedSpaceId);
    (async () => {
      try {
        const list = await venueDashboardApiService.getBusyTimes(
          venueId,
          null,
          null,
          spaceAtRequest
        );
        if (cancelled) return;
        const mapped = (Array.isArray(list) ? list : []).map((bt) => ({
          ...bt,
          start_time: bt.start_time || bt.start || bt.startTime,
          end_time: bt.end_time || bt.end || bt.endTime,
          calendar_name: bt.calendar_name || bt.calendarName || 'Calendar'
        }));
        setSpaceBusyById((prev) => ({ ...prev, [spaceAtRequest]: mapped }));
      } catch {
        if (!cancelled) {
          setSpaceBusyById((prev) => ({ ...prev, [spaceAtRequest]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [multiSpace, venueId, selectedSpaceId, busyTimes]);

  const displayBusyTimes = useMemo(() => {
    if (!multiSpace) return busyTimes || [];
    const spaceKey = String(selectedSpaceId || '');
    const source = Object.prototype.hasOwnProperty.call(spaceBusyById, spaceKey)
      ? spaceBusyById[spaceKey]
      : busyTimes || [];
    return filterBusyTimesBySpace(source, selectedSpaceId, multiSpaceCalendarsEnabled);
  }, [
    multiSpace,
    spaceBusyById,
    busyTimes,
    selectedSpaceId,
    multiSpaceCalendarsEnabled
  ]);

  const displaySelectedDateBusyTimes = useMemo(() => {
    if (!selectedDate || isNaN(selectedDate.getTime())) return [];
    if (!displayBusyTimes.length) return [];
    return getBusyTimesOverlappingDate(selectedDate, displayBusyTimes, venueTimezone);
  }, [selectedDate, displayBusyTimes, venueTimezone]);

  const displayBookings = useMemo(
    () => (multiSpace ? filterBookingsBySpace(bookings, selectedSpaceId) : bookings),
    [bookings, multiSpace, selectedSpaceId]
  );

  const displayBookingSummaryCounts = useMemo(() => {
    if (!multiSpace) return bookingSummaryCounts;
    return getBookingSummaryCounts(displayBookings);
  }, [multiSpace, bookingSummaryCounts, displayBookings]);

  useEffect(() => {
    import('./BookingsTimeline');
    import('./ManageBookingsView').then(() => {
      setManageViewMounted(true);
    });
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setIsManageBookingsOpen(false);
      setInitialBookingId(null);
      setDrawerBooking(null);
      setMobileManage(null);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!homeViewKey) return;
    setInitialBookingId(null);
    setIsManageBookingsOpen(false);
    setDrawerBooking(null);
  }, [homeViewKey]);

  useEffect(() => {
    try {
      const openKey = getScopedSessionKey(MANAGE_BOOKINGS_OPEN_KEY_PREFIX, venueId);
      const initialIdKey = getScopedSessionKey(MANAGE_BOOKINGS_INITIAL_ID_KEY_PREFIX, venueId);
      if (isManageBookingsOpen) {
        sessionStorage.setItem(openKey, '1');
        if (initialBookingId) {
          sessionStorage.setItem(initialIdKey, String(initialBookingId));
        } else {
          sessionStorage.removeItem(initialIdKey);
        }
      } else {
        sessionStorage.removeItem(openKey);
        sessionStorage.removeItem(initialIdKey);
      }
    } catch {
      // no-op if sessionStorage unavailable
    }
  }, [venueId, isManageBookingsOpen, initialBookingId]);

  useEffect(() => {
    if (!drawerBooking?.id) return;
    const latest = bookings.find((b) => String(b.id) === String(drawerBooking.id));
    if (!latest) return;
    const stamp = `${latest.updated_at}|${latest.status}|${latest.payment_status ?? latest.paymentStatus}`;
    const prevStamp = `${drawerBooking.updated_at}|${drawerBooking.status}|${drawerBooking.payment_status ?? drawerBooking.paymentStatus}`;
    if (stamp !== prevStamp) setDrawerBooking(latest);
  }, [bookings, drawerBooking]);

  const handleManageBookingsOpen = useCallback((bookingId = null) => {
    const resolvedId =
      bookingId == null || typeof bookingId === 'object' ? null : bookingId;
    import('./ManageBookingsView').then(() => setManageViewMounted(true));
    startUiTransition(() => {
      setInitialBookingId(resolvedId);
      setIsManageBookingsOpen(true);
    });
  }, [startUiTransition]);

  const handleManageBookingsClose = useCallback(() => {
    startUiTransition(() => {
      setInitialBookingId(null);
      setIsManageBookingsOpen(false);
    });
  }, [startUiTransition]);

  const handleManageBookingClick = useCallback((booking) => {
    setDrawerBooking(booking);
  }, []);

  const handleRefreshAvailabilityOrBookings = useCallback((options) => {
    onRetryBookings(options);
  }, [onRetryBookings]);

  const handleRefreshBookings = async () => {
    if (!onRetryBookings || bookingsRefreshing) return;
    setBookingsRefreshing(true);
    try {
      await onRetryBookings({ force: true });
    } finally {
      setBookingsRefreshing(false);
    }
  };

  const timelineActive = isVisible && !isManageBookingsOpen;

  return (
    <div className="home-section">
      {isPhone ? (
        <>
          {venueSpaces?.length > 1 ? (
            <div className="home-space-switcher-row home-space-switcher-row--mobile">
              <SpaceSwitcher
                spaces={venueSpaces}
                value={selectedSpaceId}
                onChange={handleSpaceChange}
              />
            </div>
          ) : null}
        <HomeMobileList
          bookings={displayBookings}
          loading={bookingsLoading}
          error={bookingsError}
          refreshing={bookingsRefreshing}
          syncLoading={syncLoading}
          onRefresh={handleRefreshBookings}
          onSyncCalendar={() =>
            syncCalendar?.({ spaceId: selectedSpaceId || undefined })
          }
          onAddBooking={() => {
            setIsManageBookingsOpen(false);
            setAddBookingSignal((value) => value + 1);
          }}
          onOpenBooking={(booking) => setDrawerBooking(booking)}
          onSectionChange={onSectionChange}
          onEnsurePastWindow={onEnsurePastWindow}
          onLoadPastBookings={onLoadPastBookings}
          loadingPastBookings={loadingPastBookings}
          hasMorePastBookings={hasMorePastBookings}
        />
        </>
      ) : null}

      {manageViewMounted ? (
        <div
          className={`home-content home-content--full-width home-desktop${
            !isManageBookingsOpen ? ' home-manage-host--parked' : ''
          }`}
          aria-hidden={!isManageBookingsOpen}
        >
          <div className="calendar-bookings-wrapper calendar-bookings-wrapper--manage">
            <Suspense fallback={<ManageBookingsFallback />}>
              <ManageBookingsView
                bookings={bookings}
                loading={bookingsLoading}
                error={bookingsError}
                onRetry={onRetryBookings}
                onBack={handleManageBookingsClose}
                onUpdateBookingStatus={onConfirmBooking}
                onPatchBookingLocal={onPatchBookingLocal}
                initialBookingId={initialBookingId}
                venueId={venueId}
                venueName={venueDetails?.name || venueDetails?.venue_name || 'Venue'}
                venuePaymentLink={venueDetails?.payment_link || ''}
                bookingRules={bookingRules}
                onOpenMessages={onOpenMessages}
                onLoadPastBookings={onLoadPastBookings}
                loadingPastBookings={loadingPastBookings}
                hasMorePastBookings={hasMorePastBookings}
                onBookingHydrated={onBookingHydrated}
                onEnsurePastWindow={onEnsurePastWindow}
                venueSpaces={venueSpaces}
                venueDetails={venueDetails}
              />
            </Suspense>
          </div>
        </div>
      ) : null}

      <div
        className={`home-content home-desktop${isManageBookingsOpen ? ' home-content--parked' : ''}`}
        aria-hidden={isManageBookingsOpen}
      >
        {venueSpaces?.length > 1 ? (
          <div className="home-space-switcher-row">
            <SpaceSwitcher
              spaces={venueSpaces}
              value={selectedSpaceId}
              onChange={handleSpaceChange}
            />
          </div>
        ) : null}
        <div className="calendar-bookings-wrapper">
          <CalendarPanel
            venueId={venueId}
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            bookings={displayBookings}
            bookingSummaryCounts={displayBookingSummaryCounts}
            busyTimes={displayBusyTimes}
            venueTimezone={venueTimezone}
            syncCalendar={() => syncCalendar?.({ spaceId: selectedSpaceId || undefined })}
            syncLoading={syncLoading}
            lastSyncTime={lastSyncTime}
          />

          <Suspense fallback={<TimelineFallback />}>
            <BookingsTimeline
              isTimelineActive={timelineActive}
              venueId={venueId}
              spaceId={selectedSpaceId || spaceId}
              venueSpaces={venueSpaces}
              defaultPrice={defaultPrice}
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              bookings={displayBookings}
              bookingsByDateIndex={multiSpace ? null : bookingsByDateIndex}
              busyTimes={displayBusyTimes}
              selectedDateBusyTimes={displaySelectedDateBusyTimes}
              syncCalendar={syncCalendar}
              syncLoading={syncLoading}
              pushCalendarToGoogle={pushCalendarToGoogle}
              pushLoading={pushLoading}
              availabilityRefreshTick={availabilityRefreshTick}
              onRefreshAvailability={onRefreshAvailability}
              availabilityRefreshing={availabilityRefreshing}
              onManageBookings={handleManageBookingsOpen}
              onManageBookingClick={handleManageBookingClick}
              operatingHours={operatingHours}
              operatingHoursPerPerson={operatingHoursPerPerson}
              holidays={holidays}
              venueDetails={venueDetails}
              dynamicPricing={dynamicPricing}
              defaultPerPersonPrice={defaultPerPersonPrice}
              dynamicPricingPerPerson={dynamicPricingPerPerson}
              bookingRules={bookingRules}
              onBookingCreated={onAddBookingLocal}
              onRefreshBookings={handleRefreshAvailabilityOrBookings}
              addBookingSignal={addBookingSignal}
            />
          </Suspense>
        </div>
        <div className="right-panel" />
      </div>

      {!isManageBookingsOpen && drawerBooking && isPhone && !mobileManage ? (
        <Suspense fallback={null}>
          <ManageBookingMobile
            booking={drawerBooking}
            onClose={() => {
              setMobileManage(null);
              setDrawerBooking(null);
            }}
            onOpenSection={setMobileManage}
          />
        </Suspense>
      ) : null}
      {!isManageBookingsOpen && drawerBooking && (!isPhone || mobileManage) ? (
        <Suspense fallback={null}>
          <HydratedManageBookingDrawer
            key={mobileManage?.key || 'desktop-booking'}
            booking={drawerBooking}
            venueId={venueId}
            venuePaymentLink={venueDetails?.payment_link || ''}
            bookingRules={bookingRules}
            initialTab={mobileManage?.tab}
            initialPriceTab={mobileManage?.priceTab}
            mobileSection={mobileManage}
            openCancelOnMount={Boolean(mobileManage?.openCancel)}
            autoStartMessage={Boolean(mobileManage?.autoMessage)}
            onHydrated={onBookingHydrated}
            onMobileBack={() => setMobileManage(null)}
            onClose={() => {
              setMobileManage(null);
              setDrawerBooking(null);
            }}
            onUpdateBookingStatus={onConfirmBooking}
            onBookingRefresh={onRetryBookings}
            onBookingLocalUpdate={onPatchBookingLocal}
            onOpenMessages={onOpenMessages}
          />
        </Suspense>
      ) : null}
    </div>
  );
});

const HomeSection = ({ isVisible = true, ...props }) => {
  const [hasShown, setHasShown] = useState(isVisible);
  const lastVisiblePropsRef = useRef(props);

  if (isVisible) {
    lastVisiblePropsRef.current = props;
  }

  useEffect(() => {
    if (isVisible) setHasShown(true);
  }, [isVisible]);

  if (!hasShown) {
    return null;
  }

  const renderProps = isVisible ? props : lastVisiblePropsRef.current;

  return (
    <div
      className={`home-section-host${isVisible ? '' : ' home-section-host--parked'}`}
      aria-hidden={!isVisible}
    >
      <HomeSectionActive {...renderProps} isVisible={isVisible} />
    </div>
  );
};

export default HomeSection;
